/**
 * Nova Derma — form scaffolding (PRD §10.3/§10.5, DS-12, DS-30…33).
 *
 * The label/control/message stack and the grid that arranges it. Nothing here
 * renders a control itself: whatever is passed as `children` is the control, so
 * the same wrapper serves Input, Select, DatePicker and a hand-rolled widget.
 */

import { cloneElement, isValidElement, useId } from 'react';
import type { HTMLAttributes, LabelHTMLAttributes, ReactElement, ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// FieldLabel
// ---------------------------------------------------------------------------

export interface FieldLabelProps extends LabelHTMLAttributes<HTMLLabelElement> {
  /** Appends the red asterisk that marks a mandatory field. */
  required?: boolean;
  children: ReactNode;
}

export function FieldLabel({ required, className, children, ...rest }: FieldLabelProps) {
  return (
    // DS-12 — every field, column and group heading speaks in the same micro caps.
    <label className={cn('label-micro block leading-none', className)} {...rest}>
      {children}
      {required ? (
        <span className="ms-0.5 text-red-500" aria-hidden>
          *
        </span>
      ) : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// FieldError
// ---------------------------------------------------------------------------

export interface FieldErrorProps extends HTMLAttributes<HTMLParagraphElement> {
  /** Arabic validation message. Renders nothing when empty, so callers need no guard. */
  children?: ReactNode;
}

export function FieldError({ className, children, ...rest }: FieldErrorProps) {
  if (!children) return null;
  return (
    <p role="alert" className={cn('text-[11px] leading-tight text-red-600', className)} {...rest}>
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// FormField
// ---------------------------------------------------------------------------

export interface FormFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  required?: boolean;
  /** Arabic validation message; also flips the control to the error palette. */
  error?: string;
  /** Helper line shown while the field is valid. */
  hint?: ReactNode;
  /** Id of the control. Omit it and the field wires the label to its child itself. */
  htmlFor?: string;
  children: ReactNode;
}

type InjectableProps = { id?: string; error?: string };

export function FormField({
  label,
  required,
  error,
  hint,
  htmlFor,
  className,
  children,
  ...rest
}: FormFieldProps) {
  const generatedId = useId();

  let control = children;
  let labelFor = htmlFor;

  if (isValidElement(children)) {
    const element = children as ReactElement<InjectableProps>;
    const patch: InjectableProps = {};

    if (htmlFor === undefined) {
      labelFor = element.props.id ?? generatedId;
      if (element.props.id === undefined) patch.id = labelFor;
    }

    // Pass the message down so a caller states the error once. Host elements are
    // skipped — an unknown `error` attribute on a <div> is a React warning.
    if (error && typeof element.type !== 'string' && element.props.error === undefined) {
      patch.error = error;
    }

    if (Object.keys(patch).length > 0) control = cloneElement(element, patch);
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)} {...rest}>
      <FieldLabel htmlFor={labelFor} required={required}>
        {label}
      </FieldLabel>
      {control}
      {/* One message line only: an error replaces the hint rather than stacking
          on top of it, so a validating form never reflows the grid. */}
      {error ? (
        <FieldError>{error}</FieldError>
      ) : hint ? (
        <p className="text-[11px] leading-tight text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FormGrid
// ---------------------------------------------------------------------------

/** Every layout collapses to a single column below `md` — a dialog is narrow. */
const GRID_COLUMNS: Record<1 | 2 | 3 | 4, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
};

/**
 * NFR-05 — a field that spans the grid at full width says so with `col-span-2`,
 * which is right at `md` and up and quietly wrong below it: `grid-column: span 2`
 * inside a one-column grid does not clamp, it *creates* a second, implicit
 * column, and the whole form then overflows the screen it was collapsed to fit.
 *
 * The span is therefore pinned to 1 exactly where the grid is 1. `!` because the
 * child's own `col-span-2` is a class of equal specificity and which of the two
 * wins would otherwise come down to Tailwind's internal ordering. It cannot
 * fight a `md:col-span-2`: the two never apply at the same width.
 */
const COLLAPSE_SPANS = 'max-md:[&>*]:!col-span-1';

export interface FormGridProps extends HTMLAttributes<HTMLDivElement> {
  cols?: 1 | 2 | 3 | 4;
}

export function FormGrid({ cols = 2, className, ...rest }: FormGridProps) {
  return (
    <div
      className={cn('grid gap-3', GRID_COLUMNS[cols], cols > 1 && COLLAPSE_SPANS, className)}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// FormSection
// ---------------------------------------------------------------------------

export interface FormSectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  /** Buttons or links pinned to the end side of the heading row. */
  actions?: ReactNode;
  children: ReactNode;
}

export function FormSection({ title, description, actions, className, children, ...rest }: FormSectionProps) {
  return (
    <section className={cn('flex flex-col gap-2', className)} {...rest}>
      <div className="flex items-start justify-between gap-2 border-b border-slate-200 pb-1">
        <div className="min-w-0">
          <h3 className="label-micro truncate">{title}</h3>
          {description ? <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}
